import { useEffect, useState } from "react";
import {
    DEFAULT_TEMPLATES,
    defaultTrustState,
    loadTrustCopy,
    saveTrustCopy,
    expandTrustCopy,
    routeTrustCopy,
} from "@/lib/trustCopy";

const TemplateRow = ({ template, enabled, xValue, onToggle, onXChange }) => {
    const preview = template.hasX
        ? template.text.replace(/X/g, xValue || template.defaultX || "X")
        : template.text;
    const len = preview.length;
    const over = len > 90;
    return (
        <div className="flex items-center gap-2 py-1.5">
            <input
                type="checkbox"
                data-testid={`tc-toggle-${template.id}`}
                checked={enabled}
                onChange={onToggle}
                className="w-4 h-4 accent-klein cursor-pointer"
            />
            <span
                className={`flex-1 text-sm font-mono truncate ${
                    enabled ? "text-ink" : "text-neutral-400"
                } ${over ? "line-through" : ""}`}
                title={preview}
            >
                {preview}
            </span>
            {template.hasX && (
                <input
                    data-testid={`tc-x-${template.id}`}
                    type="text"
                    value={xValue}
                    onChange={onXChange}
                    placeholder={template.defaultX}
                    className="w-16 h-7 px-2 border border-neutral-300 bg-white font-mono text-xs focus:border-klein focus:ring-0"
                    title={template.xLabel}
                />
            )}
            <span
                className={`font-mono text-[10px] tabular-nums w-10 text-right ${
                    over ? "text-red-500" : "text-neutral-500"
                }`}
            >
                {len}
            </span>
        </div>
    );
};

export const TrustCopyPanel = ({ onChange }) => {
    const [state, setState] = useState(() => loadTrustCopy() || defaultTrustState());

    // Persist + emit on every change
    useEffect(() => {
        saveTrustCopy(state);
        const expanded = expandTrustCopy(state);
        const routed = routeTrustCopy(expanded);
        onChange?.({ expanded, ...routed });
    }, [state, onChange]);

    const routed = routeTrustCopy(expandTrustCopy(state));

    const toggleAll = (on) => {
        const en = {};
        for (const t of DEFAULT_TEMPLATES) en[t.id] = on;
        setState((s) => ({ ...s, enabled: en }));
    };

    return (
        <div className="border border-neutral-300" data-testid="trust-copy-panel">
            <div className="flex items-center justify-between px-3 py-2 bg-neutral-50 border-b border-neutral-300">
                <span className="mono-label">Client Trust Copy · X-fill</span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="tc-select-all"
                        onClick={() => toggleAll(true)}
                        className="mono-label hover:text-ink"
                    >
                        all
                    </button>
                    <span className="text-neutral-300">·</span>
                    <button
                        type="button"
                        data-testid="tc-select-none"
                        onClick={() => toggleAll(false)}
                        className="mono-label hover:text-ink"
                    >
                        none
                    </button>
                </div>
            </div>

            <div className="px-3 py-2 max-h-56 overflow-y-auto thin-scroll">
                {DEFAULT_TEMPLATES.map((t) => (
                    <TemplateRow
                        key={t.id}
                        template={t}
                        enabled={!!state.enabled[t.id]}
                        xValue={state.xValues[t.id] ?? ""}
                        onToggle={() =>
                            setState((s) => ({
                                ...s,
                                enabled: { ...s.enabled, [t.id]: !s.enabled[t.id] },
                            }))
                        }
                        onXChange={(e) =>
                            setState((s) => ({
                                ...s,
                                xValues: { ...s.xValues, [t.id]: e.target.value },
                            }))
                        }
                    />
                ))}
            </div>

            <div className="px-3 py-2 border-t border-neutral-300">
                <label className="mono-label block mb-1">Custom (one per line)</label>
                <textarea
                    data-testid="tc-custom"
                    value={state.custom}
                    onChange={(e) => setState((s) => ({ ...s, custom: e.target.value }))}
                    placeholder={"Serving Bangalore Since 2010\nISO Certified Service"}
                    rows={2}
                    className="w-full px-2 py-1 border border-neutral-300 bg-white focus:border-klein focus:ring-0 text-xs font-mono resize-none"
                />
            </div>

            <div
                className="px-3 py-2 border-t border-neutral-300 bg-neutral-50 grid grid-cols-4 gap-2 text-center"
                data-testid="tc-routing-preview"
            >
                <div>
                    <div className="mono-label">Headlines</div>
                    <div className="font-heading text-sm font-black text-ink">
                        {routed.headlines.length}
                    </div>
                </div>
                <div>
                    <div className="mono-label">Descs</div>
                    <div className="font-heading text-sm font-black text-ink">
                        {routed.descriptions.length}
                    </div>
                </div>
                <div>
                    <div className="mono-label">Callouts</div>
                    <div className="font-heading text-sm font-black text-ink">
                        {routed.callouts.length}
                    </div>
                </div>
                <div>
                    <div className="mono-label">Rejected</div>
                    <div className="font-heading text-sm font-black text-red-500">
                        {routed.rejected.length}
                    </div>
                </div>
            </div>
        </div>
    );
};
